import { memo, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { TONE_DOTS, type ToneName } from './tone';

/**
 * Table cells as real components.
 *
 * The imported design builds these as inline factory functions called during
 * render (`cellText`, `cellBadge`, …). Defining them as components instead
 * means React can memoize them and a row re-render does not rebuild every cell
 * — which matters at 100 rows × 6 columns.
 */

/**
 * A figure the table should call attention to — a duration past its budget, a
 * failure count that is not zero. Only the ink changes: a filled cell in a
 * column of plain ones reads as a different kind of row.
 */
const VALUE_TONES = {
  ok: 'text-ok-fg',
  warn: 'text-warn-fg',
  err: 'text-err-fg',
  muted: 'text-muted-foreground',
} as const;

export type ValueTone = keyof typeof VALUE_TONES;

interface TextCellProps {
  readonly value: string | null;
  readonly sub?: string | null;
  readonly mono?: boolean;
  readonly align?: 'left' | 'right';
  readonly weight?: 'normal' | 'medium';
  readonly tone?: ValueTone;
}

export const TextCell = memo(function TextCell({
  value,
  sub,
  mono = false,
  align = 'left',
  weight = 'normal',
  tone,
}: TextCellProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5', align === 'right' && 'items-end')}>
      <span
        className={cn(
          'truncate text-[13px] text-foreground',
          // A right-aligned column is a numeric or a timestamp one — that is
          // what the alignment is for — and proportional figures in a column
          // whose job is comparison do not line up under each other.
          align === 'right' && 'tabular-nums',
          mono && 'font-mono',
          weight === 'medium' && 'font-medium',
          tone !== undefined && VALUE_TONES[tone],
        )}
      >
        {/* An em dash reads as "no value"; an empty cell reads as a bug. */}
        {value === null || value === '' ? '—' : value}
      </span>
      {sub === undefined || sub === null || sub === '' ? null : (
        <span className="truncate text-xs text-muted-foreground">{sub}</span>
      )}
    </div>
  );
});

interface BadgeCellProps {
  readonly label: string;
  readonly tone: ToneName;
  readonly dot?: boolean;
}

export const BadgeCell = memo(function BadgeCell({ label, tone, dot = false }: BadgeCellProps) {
  return (
    <Badge tone={tone} size="sm">
      {dot ? (
        <span className={cn('size-1.5 shrink-0 rounded-full', TONE_DOTS[tone])} aria-hidden="true" />
      ) : null}
      {label}
    </Badge>
  );
});

interface ChipsCellProps {
  readonly items: readonly string[];
  /** Beyond this, the rest collapse into a "+n" chip rather than wrapping. */
  readonly max?: number;
}

export const ChipsCell = memo(function ChipsCell({ items, max = 3 }: ChipsCellProps) {
  if (items.length === 0) {
    return <span className="text-sm text-muted-foreground/60">—</span>;
  }

  const shown = items.slice(0, max);
  const overflow = items.length - shown.length;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {shown.map((item) => (
        <span
          key={item}
          className="truncate rounded-md border border-border bg-background px-1.5 py-0.5 text-xs text-muted-foreground"
        >
          {item}
        </span>
      ))}
      {overflow > 0 ? (
        <span className="text-xs text-muted-foreground" title={items.join(', ')}>
          +{overflow}
        </span>
      ) : null}
    </div>
  );
});

interface PersonCellProps {
  readonly name: string | null;
  readonly sub: string;
  readonly initials: string;
}

export const PersonCell = memo(function PersonCell({ name, sub, initials }: PersonCellProps) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] leading-none font-semibold text-muted-foreground"
      >
        {initials}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[13px] font-medium text-foreground">{name ?? 'Unnamed'}</span>
        <span className="truncate font-mono text-xs text-muted-foreground">{sub}</span>
      </div>
    </div>
  );
});

interface RowActionsCellProps {
  readonly children: ReactNode;
}

/**
 * The trailing cell of controls on a row that is itself clickable.
 *
 * It stops the click from reaching the row: the row opens the record and the
 * buttons do something narrower, and an operator who aimed at "Verify" has not
 * asked to navigate away.
 */
export function RowActionsCell({ children }: RowActionsCellProps) {
  return (
    <div
      className="flex items-center justify-end gap-1"
      onClick={(event) => {
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
      role="presentation"
    >
      {children}
    </div>
  );
}
